import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { Link, useLocation } from 'react-router-dom';
import {
  AdminCategory,
  ApiError,
  ItemLookupKind,
  ItemLookupResponse,
  ItemSearchHit,
  ItemTenant,
  listCategories,
  listItemTenants,
  lookupItems,
} from '../../api';
import { useAuth } from '../../auth/AuthContext';
import ItemEditDialog from '../dialogs/ItemEditDialog';
import ItemImage from '../widgets/ItemImage';
import ItemSearchBar from '../widgets/items/ItemSearchBar';
import Price from '../widgets/Price';

// Mirrors ItemsPage's stockColor — keep both in sync. Red when out of
// stock, default colour otherwise. The "low-stock" amber tier isn't a
// thing yet here; revisit when the category page picks one up.
const stockColor = (stock: number): string | undefined => {
  if (stock > 0) return undefined;
  return '#b91c1c';
};

// Cap on results requested from the lookup endpoint. The backend
// clamps to 100 per tenant anyway; we ask for it explicitly so the
// results page has the freedom the dropdown deliberately limited (25).
const SEARCH_LIMIT = 100;

// Human labels for the match-kind chip. The lookup endpoint cascades
// SKU → UPC → name; we surface that to the operator so an exact-code
// hit reads differently from a fuzzy name hit.
const KIND_LABELS: Record<Exclude<ItemLookupKind, 'none'>, string> = {
  sku: 'SKU match',
  upc: 'UPC match',
  name: 'name match',
};

const KIND_COLORS: Record<
  Exclude<ItemLookupKind, 'none'>,
  'success' | 'info' | 'default'
> = {
  sku: 'success',
  upc: 'success',
  name: 'default',
};

const ItemsSearchPage: React.FC = () => {
  const { idToken } = useAuth();
  const location = useLocation();
  const query = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('q')?.trim() ?? '';
  }, [location.search]);

  const [response, setResponse] = useState<ItemLookupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Tenant metadata for the per-card chip + the edit dialog's
  // image_root / sku_prefix props. Tracked as nullable so we can gate
  // the result grid on "tenants loaded" — otherwise a fast `lookupItems`
  // beats `listItemTenants`, `image_root` falls back to '', and the
  // <img> URL renders as `…sirv.com//imgXXX.jpg` (broken) until the
  // tenants response lands. The CategoryPanel uses the same null-gate.
  const [tenants, setTenants] = useState<ItemTenant[] | null>(null);
  // Per-tenant category cache — populated lazily when the operator
  // opens a hit from that tenant. Same pattern as ItemSearchBar.
  const [catsByTenant, setCatsByTenant] = useState<
    Record<string, AdminCategory[]>
  >({});
  const [pickedHit, setPickedHit] = useState<ItemSearchHit | null>(null);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    listItemTenants(idToken)
      .then((rows) => {
        if (!cancelled) setTenants(rows);
      })
      .catch(() => {
        // Fall through with an empty list so we don't block the page
        // forever — image URLs will fall back to no-image placeholders.
        if (!cancelled) setTenants([]);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  // Run the lookup whenever `q` changes. Cancelled on re-fire so a
  // typed-too-fast operator doesn't see results from a previous query
  // land after the latest one.
  useEffect(() => {
    if (!idToken || query === '') {
      setResponse(null);
      setLoading(false);
      setError('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    lookupItems(idToken, query, SEARCH_LIMIT)
      .then((r) => {
        if (!cancelled) setResponse(r);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? `API error ${e.status}`
            : (e as Error)?.message || 'lookup failed',
        );
        setResponse(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, query]);

  // Fetch categories for the picked hit's tenant on demand. Mirrors
  // the same lazy-cache pattern as ItemSearchBar — keeps the edit
  // dialog's Category dropdown populated cross-tenant without paying
  // up-front for every tenant's category tree.
  useEffect(() => {
    if (!pickedHit || !idToken) return;
    const tenant = pickedHit.tenant;
    if (catsByTenant[tenant] !== undefined) return;
    let cancelled = false;
    listCategories(idToken, tenant)
      .then((cats) => {
        if (!cancelled) {
          setCatsByTenant((prev) => ({ ...prev, [tenant]: cats }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatsByTenant((prev) => ({ ...prev, [tenant]: [] }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pickedHit, idToken, catsByTenant]);

  const tenantEntry = useMemo(
    () =>
      pickedHit && tenants
        ? tenants.find((t) => t.slug === pickedHit.tenant)
        : undefined,
    [pickedHit, tenants],
  );
  const categoriesReady =
    pickedHit !== null && catsByTenant[pickedHit.tenant] !== undefined;

  return (
    <Stack spacing={3}>
      <Breadcrumbs aria-label="search breadcrumb">
        <MuiLink
          component={Link}
          to="/items"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Items
        </MuiLink>
        <Typography sx={{ color: '#1f2937', fontWeight: 500 }}>
          {query ? `Search: "${query}"` : 'Search'}
        </Typography>
      </Breadcrumbs>

      <ItemSearchBar initialQuery={query} />

      {query === '' && (
        <Typography
          variant="body2"
          sx={{ color: '#64748b', fontStyle: 'italic' }}
        >
          Type a SKU, UPC, or name in the search bar above and press Enter
          to populate this page.
        </Typography>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {(loading || (query !== '' && tenants === null)) && (
        // Spin until BOTH the lookup AND the tenants list have landed.
        // Rendering the result grid without tenants would build broken
        // Sirv URLs (empty `image_root` → `…sirv.com//imgXXX.jpg`)
        // that would heal on the next render — visible flicker.
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && response && query !== '' && tenants !== null && (
        <SearchResults
          response={response}
          tenants={tenants}
          onPick={setPickedHit}
        />
      )}

      <ItemEditDialog
        open={pickedHit !== null && categoriesReady}
        tenant={pickedHit?.tenant ?? ''}
        imageRoot={tenantEntry?.image_root ?? ''}
        skuPrefix={tenantEntry?.sku_prefix ?? ''}
        itemId={pickedHit?.id ?? null}
        categories={
          pickedHit ? (catsByTenant[pickedHit.tenant] ?? []) : []
        }
        onClose={() => setPickedHit(null)}
        onSaved={() => {
          // Re-run the lookup so an edited item's name/price/etc. shows
          // up immediately. Cheap — backend hit is fast and the result
          // shape is small.
          if (idToken && query !== '') {
            lookupItems(idToken, query, SEARCH_LIMIT)
              .then(setResponse)
              .catch(() => {});
          }
        }}
      />
    </Stack>
  );
};

const SearchResults: React.FC<{
  response: ItemLookupResponse;
  tenants: ItemTenant[];
  onPick: (hit: ItemSearchHit) => void;
}> = ({ response, tenants, onPick }) => {
  if (response.kind === 'none' || response.hits.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ color: '#64748b', fontStyle: 'italic', textAlign: 'center', py: 4 }}
      >
        No matches.
      </Typography>
    );
  }
  const kindLabel = KIND_LABELS[response.kind];
  const kindColor = KIND_COLORS[response.kind];

  return (
    <>
      <Typography variant="body2" sx={{ color: '#64748b' }}>
        {response.hits.length} {kindLabel}
        {response.hits.length === 1 ? '' : 'es'}
      </Typography>
      <Grid container spacing={2}>
        {response.hits.map((hit) => (
          <Grid key={`${hit.tenant}-${hit.id}`} item xs={6} sm={4} md={3} lg={2}>
            <ResultCard
              hit={hit}
              tenants={tenants}
              kindLabel={kindLabel}
              kindColor={kindColor}
              onPick={onPick}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};

const ResultCard: React.FC<{
  hit: ItemSearchHit;
  tenants: ItemTenant[];
  kindLabel: string;
  kindColor: 'success' | 'info' | 'default';
  onPick: (hit: ItemSearchHit) => void;
}> = ({ hit, tenants, kindLabel, kindColor, onPick }) => {
  const tenant = tenants.find((t) => t.slug === hit.tenant);
  const imageRoot = tenant?.image_root ?? '';
  const tenantLabel = tenant?.label ?? hit.tenant;
  // If the hit's tenant isn't in our list (shouldn't happen — tenant
  // slugs are pinned server-side — but possible if a tenant was just
  // disabled mid-session), suppress the image rather than ship a Sirv
  // URL with an empty image_root segment. ItemImage's `id == null`
  // branch renders the title placeholder, which reads cleanly.
  const safeImageId = tenant ? hit.image_id : null;
  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        // Same dim treatment as the category-grid hidden cards
        // (ItemsPage.tsx:947). Keeps a consistent visual language for
        // "operator-hidden" across both surfaces.
        opacity: hit.hidden ? 0.55 : 1,
      }}
    >
      <CardActionArea onClick={() => onPick(hit)}>
        <Box sx={{ p: 1.5 }}>
          <ItemImage
            id={safeImageId}
            imageRoot={imageRoot}
            title={hit.name}
            size={140}
          />
          <Chip
            label={kindLabel}
            size="small"
            color={kindColor}
            variant={kindColor === 'default' ? 'outlined' : 'filled'}
            sx={{ position: 'absolute', top: 8, left: 8, fontSize: '0.65rem', height: 20 }}
          />
          <Chip
            label={tenantLabel}
            size="small"
            variant="outlined"
            sx={{ position: 'absolute', top: 8, right: 8, fontSize: '0.65rem', height: 20 }}
          />
          {hit.brand && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: '#64748b', mt: 1 }}
            >
              {hit.brand}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: '#1f2937',
              wordBreak: 'break-word',
              minHeight: '2.5em',
            }}
          >
            {hit.name}
          </Typography>
          {hit.hidden && (
            <Chip
              label="hidden"
              size="small"
              sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }}
            />
          )}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mt: 1 }}
          >
            <Box>
              <Price price={hit.price ?? 0} showZero />
            </Box>
            <Typography
              variant="caption"
              sx={{ color: stockColor(hit.stock), fontWeight: 500 }}
            >
              {hit.stock} in stock
            </Typography>
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default ItemsSearchPage;
